import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import classnames from 'classnames/bind'
import { Autocomplete } from '@base-ui-components/react/autocomplete'
import { Icon } from '@adc/ui-morpheus'
import type { primitives } from '@adc/blueprint'
import { usePermissionsInstance } from '../../authorization/PermissionsProvider'
import {
  resolveNavigation,
  ResolvedItem,
  ResolvedGroup,
} from '../../authorization/resolveNavigation'
import { isFuzzyMatch } from './fuzzyMatch'
import {
  ScopeToken,
  pendingScopeFragment,
  resolveTabCompletion,
  completionsFor,
  toSearchTypes,
} from './scopeTokens'
import { usePlatformSearch, PlatformSearchResult } from './usePlatformSearch'
import { useCommandPalette } from './useCommandPalette'
import { urnToRoute } from './urnToRoute'
import { usePrefetchOnOpen } from './prefetchRoutes'
import styles from './styles.scss'

const cx = classnames.bind(styles)

// Shift-held quick-jump: digit hints for the first 9 rows.
const HINT_CHARS = '123456789'.split('')

interface StoreShape {
  user: primitives.AuthenticatedAuctionUser
  featureFlags?: Record<string, boolean>
}

/**
 * A palette item fed to Base UI's Autocomplete. The `{ value, label }` shape is
 * the convention Base UI reads automatically for identity + display; `row`
 * carries the original nav command or data result for navigation on select.
 */
interface PaletteItem {
  readonly value: string
  readonly label: string
  readonly row:
    | { kind: 'nav'; item: ResolvedItem }
    | { kind: 'data'; result: PlatformSearchResult }
}

/** Filter nav items by fuzzy matching query against label + keywords. */
function filterItems(
  items: ReadonlyArray<ResolvedItem>,
  query: string
): ReadonlyArray<ResolvedItem> {
  if (!query.trim()) return items
  return items.filter(
    item =>
      isFuzzyMatch(query, item.label) ||
      item.keywords.some(kw => isFuzzyMatch(query, kw))
  )
}

/** Group filtered nav items by their `group` field, preserving order. */
function groupFilteredItems(
  items: ReadonlyArray<ResolvedItem>
): ReadonlyArray<ResolvedGroup> {
  const map = new Map<string, ResolvedItem[]>()
  items.forEach(item => {
    const existing = map.get(item.group)
    if (existing) existing.push(item)
    else map.set(item.group, [item])
  })
  return Array.from(map.entries()).map(([group, groupItems]) => ({
    group,
    items: groupItems,
  }))
}

const SearchIcon: React.FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='11' cy='11' r='8' />
    <line x1='21' y1='21' x2='16.65' y2='16.65' />
  </svg>
)

const ArrowRightIcon: React.FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line x1='5' y1='12' x2='19' y2='12' />
    <polyline points='12 5 19 12 12 19' />
  </svg>
)

export const CommandPalette: React.FC = () => {
  // ── Store + router wiring (react-redux 8 + react-router-dom 5 hooks). ───────
  const { open, onClose } = useCommandPalette()
  const history = useHistory()
  const user = useSelector((store: StoreShape) => store.user)
  const featureFlags = useSelector(
    (store: StoreShape) => store.featureFlags ?? {}
  )

  const isInternallyTrusted = user.is_current_user_internally_trusted ?? false
  const permissions = usePermissionsInstance()

  const onNavigate = useCallback(
    (path: string, fullRefresh?: boolean): void => {
      if (fullRefresh) {
        window.location.href = path
      } else {
        history.push(path)
      }
    },
    [history]
  )

  const [query, setQuery] = useState('')
  const [scopes, setScopes] = useState<ScopeToken[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  // Shift-held quick-jump: while Shift is down, the first 9 rows show a 1–9
  // badge; pressing that digit (Shift+N) jumps straight to the row.
  const [shiftHeld, setShiftHeld] = useState(false)

  // ── Universal search (thin client; server does everything). ────────────────
  const {
    groups: dataGroups,
    classification,
    isSearching,
    search: runSearch,
    clear: clearSearch,
  } = usePlatformSearch()

  // Fire the search whenever the query or the locked scopes change.
  useEffect(() => {
    runSearch(query, toSearchTypes(scopes))
  }, [query, scopes, runSearch])

  // ── Nav commands (existing behavior). ──────────────────────────────────────
  const { items: visibleItems } = useMemo(
    () => resolveNavigation({ permissions, isInternallyTrusted, featureFlags }),
    [permissions, isInternallyTrusted, featureFlags]
  )

  // When scoped to a data entity type, suppress nav commands — the user is
  // clearly hunting for data, not pages.
  const navItems = useMemo(
    () => (scopes.length > 0 ? [] : filterItems(visibleItems, query)),
    [visibleItems, query, scopes.length]
  )
  const navGroups = useMemo(() => groupFilteredItems(navItems), [navItems])

  // Warm the chunks for the nav commands currently shown while the palette is
  // open, so selecting one navigates instantly.
  const navPaths = useMemo(() => navItems.map(item => item.path), [navItems])
  usePrefetchOnOpen(open, navPaths)

  // ── View models carrying Base UI palette items (data first, then nav). ─────
  const dataGroupVMs = useMemo(
    () =>
      dataGroups.map(group => ({
        type: group.type,
        label: group.label,
        total: group.total,
        shown: group.results.length,
        items: group.results.map<PaletteItem>(result => ({
          value: `data:${result.relates_to_urn}`,
          label: result.label,
          row: { kind: 'data', result },
        })),
      })),
    [dataGroups]
  )
  const navGroupVMs = useMemo(
    () =>
      navGroups.map(({ group, items }) => ({
        group,
        items: items.map<PaletteItem>(item => ({
          value: `nav:${item.key}`,
          label: item.label,
          row: { kind: 'nav', item },
        })),
      })),
    [navGroups]
  )

  // Flat list in render order — Base UI uses this for arrow-key highlight order.
  const allItems = useMemo(
    () => [
      ...dataGroupVMs.flatMap(g => g.items),
      ...navGroupVMs.flatMap(g => g.items),
    ],
    [dataGroupVMs, navGroupVMs]
  )

  // Quick-jump hint maps. Only enabled when the whole list fits in 1–9 — past
  // that we don't bother (no letter hints). Match on `e.code` (Digit1…Digit9)
  // so it's shift- and layout-agnostic — Shift+1 emits '!' but the code stays.
  const quickJumpEnabled = allItems.length > 0 && allItems.length < 10
  const hintByValue = useMemo(() => {
    const m = new Map<string, string>()
    if (!quickJumpEnabled) return m
    allItems.forEach((pi, i) => m.set(pi.value, HINT_CHARS[i]))
    return m
  }, [allItems, quickJumpEnabled])
  const itemByHintCode = useMemo(() => {
    const m = new Map<string, PaletteItem>()
    if (!quickJumpEnabled) return m
    allItems.forEach((pi, i) => m.set(`Digit${HINT_CHARS[i]}`, pi))
    return m
  }, [allItems, quickJumpEnabled])

  // Reset on open.
  useEffect(() => {
    if (open) {
      setQuery('')
      setScopes([])
      setShiftHeld(false)
      clearSearch()
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, clearSearch])

  const navigate = useCallback(
    (pi: PaletteItem): void => {
      if (pi.row.kind === 'nav') {
        if (!pi.row.item.enabled) return
        onNavigate(pi.row.item.path, pi.row.item.fullRefresh)
        onClose()
        return
      }
      const route = urnToRoute(pi.row.result.relates_to_urn)
      if (route) {
        onNavigate(route.path, route.fullRefresh)
        onClose()
      }
    },
    [onNavigate, onClose]
  )

  /** Lift a completed `t:<scope>` token into a pill. Returns true if handled. */
  const tryLockScope = useCallback((): boolean => {
    const fragment = pendingScopeFragment(query)
    if (fragment == null) return false
    const token = resolveTabCompletion(fragment)
    if (!token) return false
    setScopes(prev =>
      prev.some(s => s.type === token.type) ? prev : [...prev, token]
    )
    setQuery('')
    return true
  }, [query])

  /** Input-level key handling for scope pills (runs before Base UI's handler). */
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      // Tab completes a pending scope token into a pill.
      if (e.key === 'Tab' && pendingScopeFragment(query) != null) {
        if (tryLockScope()) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
      }
      // Backspace at the start of an empty query removes the last pill.
      if (e.key === 'Backspace' && query.length === 0 && scopes.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        setScopes(prev => prev.slice(0, -1))
      }
    },
    [query, scopes.length, tryLockScope]
  )

  if (!open) return null

  const pendingFragment = pendingScopeFragment(query)
  const scopeSuggestions =
    pendingFragment != null ? completionsFor(pendingFragment) : []

  const renderItem = (pi: PaletteItem): React.ReactNode => {
    const isDisabled = pi.row.kind === 'nav' && !pi.row.item.enabled
    const missing = pi.row.kind === 'nav' ? pi.row.item.missing : []
    const hint = shiftHeld ? hintByValue.get(pi.value) : undefined
    // Left stack: label on top, sublabel beneath. The graph decides when a
    // sublabel adds context (matched email, live bid, etc.).
    const result = pi.row.kind === 'data' ? pi.row.result : undefined
    const sublabel = result?.sublabel
    // Right slot: for nav rows, the authority required to reach the page —
    // plain text, no tone. Data rows have no right-slot indicator.
    const indicator = result ? undefined : missing.join(' / ') || undefined

    let leading: React.ReactNode
    if (hint) {
      leading = (
        <span className={cx('item-hint')} aria-hidden>
          {hint}
        </span>
      )
    } else if (isDisabled) {
      leading = <Icon name='lock' color='gray' />
    } else {
      leading = <ArrowRightIcon />
    }

    return (
      <Autocomplete.Item
        key={pi.value}
        value={pi}
        disabled={isDisabled}
        className={cx('item', { 'item-disabled': isDisabled })}
        onClick={() => navigate(pi)}
      >
        {leading}
        <span className={cx('item-text')}>
          <span className={cx('item-label')}>{pi.label}</span>
          {sublabel && <span className={cx('item-sublabel')}>{sublabel}</span>}
        </span>
        {indicator && (
          <span className={cx('item-indicator', 'is-text')} title={indicator}>
            {indicator}
          </span>
        )}
      </Autocomplete.Item>
    )
  }

  // Shift-held quick-jump. Capture phase so we run before Base UI's Input /
  // List key handling; when a hint digit matches we swallow the event so the
  // Shift+N symbol (e.g. '!') never lands in the search field.
  const handleKeyDownCapture = (e: React.KeyboardEvent): void => {
    if (!quickJumpEnabled) return
    if (e.key === 'Shift') {
      setShiftHeld(true)
      return
    }
    if (e.shiftKey) {
      const item = itemByHintCode.get(e.code)
      if (item) {
        e.preventDefault()
        e.stopPropagation()
        navigate(item)
      }
    }
  }
  const handleKeyUpCapture = (e: React.KeyboardEvent): void => {
    if (e.key === 'Shift') setShiftHeld(false)
  }

  return (
    <div
      className={cx('overlay')}
      onClick={e => {
        if (e.target === e.currentTarget) onClose()
      }}
      role='presentation'
    >
      <div
        className={cx('dialog')}
        role='dialog'
        aria-modal='true'
        aria-label='Command Palette'
        onKeyDownCapture={handleKeyDownCapture}
        onKeyUpCapture={handleKeyUpCapture}
      >
        <Autocomplete.Root
          items={allItems}
          mode='none'
          value={query}
          onValueChange={value => setQuery(value)}
          open
          onOpenChange={next => {
            if (!next) onClose()
          }}
          autoHighlight='always'
          modal={false}
        >
          <div className={cx('input-wrapper')}>
            <span className={cx('search-icon')}>
              <SearchIcon />
            </span>
            {/* Locked scope pills — backspace at caret 0 removes the last one. */}
            {scopes.map(scope => (
              <span key={scope.type} className={cx('scope-pill')}>
                {scope.label}
                <button
                  type='button'
                  className={cx('scope-pill-remove')}
                  aria-label={`Remove ${scope.label} filter`}
                  onClick={() =>
                    setScopes(prev => prev.filter(s => s.type !== scope.type))
                  }
                >
                  ×
                </button>
              </span>
            ))}
            <Autocomplete.Input
              ref={inputRef}
              className={cx('input')}
              placeholder={
                scopes.length
                  ? `Search ${scopes.map(s => s.label).join(', ')}…`
                  : 'Search anything — address, event code, id, name… (t: to scope)'
              }
              onKeyDown={handleInputKeyDown}
              aria-label='Search commands and data'
              autoComplete='off'
              spellCheck={false}
            />
            {isSearching && <span className={cx('spinner')} aria-hidden />}
          </div>

          {/* Scope token completion hints (superuser affordance). */}
          {scopeSuggestions.length > 0 && (
            <div className={cx('scope-hints')}>
              {scopeSuggestions.map(s => (
                <span key={s.type} className={cx('scope-hint')}>
                  <kbd className={cx('kbd')}>t:{s.token}</kbd> {s.label}
                </span>
              ))}
              <span className={cx('scope-hint-tab')}>
                <kbd className={cx('kbd')}>Tab</kbd> to filter
              </span>
            </div>
          )}

          <Autocomplete.List className={cx('list-wrapper')}>
            <Autocomplete.Empty className={cx('empty')}>
              <div className={cx('empty-message')}>
                {isSearching ? 'Searching…' : 'No results found.'}
              </div>
            </Autocomplete.Empty>

            {/* Data groups from the universal search. */}
            {dataGroupVMs.map(group => (
              <Autocomplete.Group
                key={`data-${group.type}`}
                items={group.items}
                className={cx('group')}
              >
                <Autocomplete.GroupLabel className={cx('group-heading')}>
                  {group.label}
                  {group.total > group.shown && (
                    <span className={cx('group-count')}>
                      {' '}
                      {group.shown} of {group.total}
                    </span>
                  )}
                </Autocomplete.GroupLabel>
                {group.items.map(renderItem)}
              </Autocomplete.Group>
            ))}

            {/* Static nav commands (suppressed when scoped to data). */}
            {navGroupVMs.map(({ group, items }) => (
              <Autocomplete.Group
                key={group}
                items={items}
                className={cx('group')}
              >
                <Autocomplete.GroupLabel className={cx('group-heading')}>
                  {group}
                </Autocomplete.GroupLabel>
                {items.map(renderItem)}
              </Autocomplete.Group>
            ))}
          </Autocomplete.List>

          <div className={cx('footer')}>
            <span className={cx('shortcut-hint')}>
              <kbd className={cx('kbd')}>↑↓</kbd> navigate
            </span>
            <span className={cx('shortcut-hint')}>
              <kbd className={cx('kbd')}>↵</kbd>{' '}
              {classification?.auto_target ? 'go' : 'open'}
            </span>
            <span className={cx('shortcut-hint')}>
              <kbd className={cx('kbd')}>t:</kbd>+
              <kbd className={cx('kbd')}>Tab</kbd> filter
            </span>
            {quickJumpEnabled && (
              <span className={cx('shortcut-hint')}>
                <kbd className={cx('kbd')}>shift</kbd>+
                <kbd className={cx('kbd')}>1–9</kbd> jump
              </span>
            )}
            <span className={cx('shortcut-hint')}>
              <kbd className={cx('kbd')}>esc</kbd> close
            </span>
          </div>
        </Autocomplete.Root>
      </div>
    </div>
  )
}
